import { useEffect, useState } from "react";
import {
  FaHome,
  FaUserFriends,
  FaArrowRight,
  FaCheckCircle,
  FaUser,
  FaCalendarTimes,
} from "react-icons/fa";
import { LiaCheckCircle } from "react-icons/lia";
import { PiNumberCircleOneLight, PiNumberCircleTwoLight, PiNumberCircleThreeLight } from "react-icons/pi";
import { Link, useNavigate } from "react-router-dom";
import { getAccessToken } from "../../storage/storage";
import axios from "axios";
import Swal from "sweetalert2";
import { FormatDate } from "../../utils/FormateDate";
import { TableSkelton } from "../../components/Skeletons";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { GoOrganization } from "react-icons/go";
import { FiHome, FiUser, FiSettings, FiUsers, FiCalendar, FiCheckSquare, FiEdit } from "react-icons/fi";
import CommentModal from "./components/CommentModal";
import PageLoader from "../../components/PageLoader"; // Import your PageLoader component

export default function Dashboard() {
  const token = getAccessToken();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const Navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [cleaners, setCleaners] = useState([]);
  const [unassignedGuestStays, setUnassignedGuestStays] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [propertiesInvoices, setPropertiesInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [unassignedLoading, setUnassignedLoading] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const PropertyInfo = JSON.parse(sessionStorage.getItem("PropertyData"));
  const CleanerInfo = JSON.parse(sessionStorage.getItem("CleanerData"));
  const companyInfo = JSON.parse(sessionStorage.getItem("companyInfo"));

  // Check if all APIs have loaded
  const allDataLoaded = !statsLoading && !propertiesLoading && !loading && !unassignedLoading;

  const filteredCleaners = cleaners.filter((stay) =>
    stay.property_name.toLowerCase().includes(search.toLowerCase())
  );

  const HandleStays = () => {
    sessionStorage.removeItem("cleanerEdit");
    Navigate("/stays/add-stays");
  };

  // Reusable function to fetch dashboard stats
  const fetchDashboardStats = async () => {
    setStatsLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/api/user/dashboard-stats/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  // Fetch dashboard stats on component mount
  useEffect(() => {
    fetchDashboardStats();
  }, []);

  // Fetch properties with pending invoices
  useEffect(() => {
    setPropertiesLoading(true);
    axios
      .get(`${BASE_URL}/api/user/properties/invoice/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setPropertiesInvoices(response.data);
        setPropertiesLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching properties invoices:", error);
        setPropertiesLoading(false);
      });
  }, []);

  // Fetch stays
  useEffect(() => {
    setLoading(true);
    axios
      .get(`${BASE_URL}/api/stays/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setCleaners(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching options:", error);
        setLoading(false);
      });
  }, []);

  // Fetch unassigned guest stays
  useEffect(() => {
    setUnassignedLoading(true);
    axios
      .get(`${BASE_URL}/api/unassigned-guest-stays/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        console.log("unassigned-guest-stays " + response.data);
        setUnassignedGuestStays(response.data);
        setUnassignedLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching options:", error);
        setUnassignedLoading(false);
      });
  }, []);

  const handleTaskSheetClick = (id) => {
    Navigate(`/task-sheet/${id}`);
  };

  const handleInvoiceClick = (guestStayId) => {
    Navigate(`/invoice/${guestStayId}`);
  };

  const handleAddCleaner = (stay) => {
    sessionStorage.setItem("cleanerEdit", JSON.stringify(true));
    Navigate("/stays/add-stays", { state: { stay } });
  };

  const handleViewAllStays = () => {
    Navigate("/stays");
  };

  const handleViewAllInvoices = () => {
    Navigate("/invoices");
  };

  // Handle approve invoice
  const handleApproveInvoice = async (invoiceId) => {
    try {
      const response = await axios.put(
        `${BASE_URL}/api/invoices/${invoiceId}/approve/`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Remove the approved invoice from the list
      setPropertiesInvoices(prev =>
        prev.map(property => ({
          ...property,
          pending_invoices: property.pending_invoices.filter(inv => inv.id !== invoiceId),
          pending_invoices_count: property.pending_invoices_count -
            (property.pending_invoices.some(inv => inv.id === invoiceId) ? 1 : 0)
        }))
      );
// Refresh dashboard stats after approval
      fetchDashboardStats();
      // Show success message
      Swal.fire({
        icon: "success",
        title: response?.data?.message || "Invoice Approved",
        timer: 2000,
        showConfirmButton: false,
        position: "center",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: error.response?.data?.detail || "Something went wrong!",
        timer: 2000,
        showConfirmButton: false,
        position: "center",
      });
    }
  };

  // Handle reject invoice - open modal
  const handleRejectInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setIsOpenModal(true);
  };

  const getPropertyColor = (color) => {
    const colorMap = {
      "Pink": "#FF69B4",
      "Teal": "#008080",
      "Blue": "#0000FF",
      "Green": "#008000",
      "Purple": "#800080",
      "Orange": "#FFA500",
      "Red": "#FF0000",
      "Yellow": "#FFD700",
    };
    return colorMap[color] || "#6B7280"; // Default gray color
  };

  // Handle reject confirmation from modal
  const handleRejectConfirm = (invoiceId) => {
    // Remove the rejected invoice from the list
    setPropertiesInvoices(prev =>
      prev.map(property => ({
        ...property,
        pending_invoices: property.pending_invoices.filter(inv => inv.id !== invoiceId),
        pending_invoices_count: property.pending_invoices_count -
          (property.pending_invoices.some(inv => inv.id === invoiceId) ? 1 : 0)
      }))
    );
    fetchDashboardStats();
    setIsOpenModal(false);
    setSelectedInvoice(null);
  };

  // Generate invoices for approval from the new API response
  const generateInvoicesForApproval = () => {
    const invoices = [];

    propertiesInvoices.forEach(property => {
      property.pending_invoices.forEach(invoice => {
        invoices.push({
          id: invoice.id,
          invoiceNumber: invoice.invoice_number,
          cleanerName: `${invoice.cleaner.first_name} ${invoice.cleaner.surname}`,
          serviceType: "Cleaning Service",
          propertyName: invoice.property,
          amount: invoice.total_amount,
          completionDate: invoice.created_at ? FormatDate(invoice.created_at) : "N/A",
          status: invoice.status.toUpperCase(),
          comments: invoice.comments,
          cleanerEmail: invoice.cleaner.email,
          subtotal: invoice.subtotal
        });
      });
    });

    return invoices;
  };

  const invoicesForApproval = generateInvoicesForApproval();

  // Calculate completion status
  const completedTasks = [companyInfo, PropertyInfo, CleanerInfo].filter(Boolean).length;
  const totalTasks = 3;
  const completionPercentage = (completedTasks / totalTasks) * 100;
  const isSetupComplete = completedTasks === totalTasks;

  // Show loader until all data is loaded
  if (!allDataLoaded) {
    return <PageLoader />;
  }

  return (
    <div className="flex flex-col gap-6 px-6 w-full h-full pb-8">

      {/* Full Width Important Information */}
      <div className="w-full">
        {unassignedGuestStays?.unassigned_stays?.length > 0 ? (
          unassignedGuestStays.unassigned_stays.map((stay) => (
            <div
              key={stay.id}
              className="bg-[#FFF5CE]  text-blue-800 p-4 rounded-lg w-full"
              role="alert"
            >
              <div className="flex items-start">
                <div>
                  <p className="font-bold text-[#586989]">Important Information </p>
                  <p className="font-bold text-red-500">{stay.property_name} </p>
                  <p className="text-sm text-red-500">Stay reference {stay.id} requires to assign cleaner</p>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-[#FFF5CE] border-l-4 border-blue-500 text-blue-800 p-4 rounded-r-lg w-full">
            <div className="flex items-start">
              <FaCheckCircle className="text-blue-500 text-xl mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="font-semibold text-[#586989]">Welcome to Your Dashboard!</p>
                <p className="text-sm text-[#586989]">All upcoming stays are currently assigned. You're all set!</p>

              </div>
            </div>
          </div>
        )}
      </div>

      {/* Full Width Dashboard Stats Cards */}
      <div className="w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
          {/* Total Properties Card */}
          <div
          onClick={() => Navigate("/settings/properties")}
          className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm w-full cursor-pointer hover:shadow-md hover:bg-gray-50 transition-all duration-200"
        >
          <div className="flex flex-col items-center justify-center">
            <div className="bg-[#FFF5CE] p-3 rounded-lg mb-4">
              <FiHome className="text-[#586989] text-2xl" />
            </div>
            <p className="text-3xl font-bold text-[#586989] mb-2">
              {statsLoading ? "..." : (dashboardStats?.total_properties || 0)}
            </p>
            <p className="text-sm text-gray-600 font-medium">Total Properties</p>
            {dashboardStats?.total_properties <= 0 && <p className="text-xs text-gray-600 font-xs">
              Ready to add your first property
            </p>}
          </div>
          </div>
          {/* Active Cleaners Card */}
          <div
          onClick={() => Navigate("/settings/cleaners")}
          className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm w-full cursor-pointer hover:shadow-md hover:bg-gray-50 transition-all duration-200"
        >
          <div className="flex flex-col items-center justify-center">
            <div className="bg-[#FFF5CE] p-3 rounded-lg mb-4">
              <FiUsers className="text-[#586989] text-2xl" />
            </div>
            <p className="text-3xl font-bold text-[#586989] mb-2">
              {statsLoading ? "..." : (dashboardStats?.total_active_cleaners || 0)}
            </p>
            <p className="text-sm text-gray-600 font-medium">Active Cleaners</p>
            {dashboardStats?.total_active_cleaners <= 0 && <p className="text-xs text-gray-600 font-xs">
              Invite cleaners to get started
            </p>}
          </div>
        </div>







          {/* Stays Without Cleaners Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm w-full">
            <div className="flex flex-col items-center justify-center">
              <div className="bg-[#FFF5CE] p-3 rounded-lg mb-4">
                <FiCalendar className="text-[#586989] text-2xl" />
              </div>
              <p className="text-3xl font-bold text-[#586989] mb-2">
                {statsLoading ? "..." : (dashboardStats?.total_stays_without_cleaners || 0)}
              </p>
              <p className="text-sm text-gray-600 font-medium">Stays Without Cleaners</p>
              <p className="text-xs text-gray-600 font-xs">Stay requiring cleaner assignment</p>
            </div>
          </div>

          {/* Invoices for Approval Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm w-full">
            <div className="flex flex-col items-center justify-center">
              <div className="bg-[#FFF5CE] p-3 rounded-lg mb-4">
                <FiEdit className="text-[#586989] text-2xl" />
              </div>
              <p className="text-3xl font-bold text-[#586989] mb-2">
                {statsLoading ? "..." : (dashboardStats?.total_pending_invoices || 0)}
              </p>
              <p className="text-sm text-gray-600 font-medium">Invoices for Approval</p>
              <p className="text-xs text-gray-600 font-xs">Tasks requiring approval</p>
            </div>
          </div>
        </div>
      </div>

      {/* Two Column Layout for Content */}
      <div className="flex flex-col lg:flex-row gap-6 w-full">
        {/* Left Column - Upcoming Stays & Invoices */}
        <div className="flex-1">
          {/* Upcoming Stays Section */}
          <div className=" bg-white border border-gray-200 rounded-lg shadow-sm mb-6">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-[#586989]">Upcoming Stays</h2>
              <div className="flex items-center gap-4">
                <button
                  onClick={handleViewAllStays}
                  className="text-sm text-[#586989] hover:text-blue-800 flex items-center gap-1"
                >
                  View All <FaArrowRight className="text-xs" />
                </button>
              </div>
            </div>

            {/* Scrollable content area */}
            <div className="p-4 max-h-[645px] overflow-y-auto">
              <div className="space-y-4">
                {loading ? (
                  <div className="flex justify-center items-center h-32">
                    <TableSkelton />
                  </div>
                ) : filteredCleaners.length > 0 ? (
                  filteredCleaners.map((stay, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-3">
                          <div className="bg-gray-100 px-3 py-1 rounded border" style={{ backgroundColor: getPropertyColor(stay?.property_details?.property_color), opacity: 0.6 }}>
                            <span className="text-sm font-medium" style={{ color: 'white' }}>#{stay.id}</span>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${stay.status === 'CONFIRMED' ? 'bg-green-100 text-green-800' :
                            stay.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                            stay.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                              stay.status === 'ACCEPTED' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                            }`}>
                            {stay.status || 'Pending'}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <FaHome className="text-gray-400 text-sm" />
                            <p className="text-sm text-blue-600 ml-1">{stay.property_name}</p>
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <FaCalendarTimes className="text-gray-400 text-sm" />
                            <span className="text-sm font-medium text-gray-700">Check-in</span>
                          </div>
                          <p className="text-sm text-gray-600 ml-6">{FormatDate(stay.check_in_date)}</p>
                        </div>

                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <FaCalendarTimes className="text-gray-400 text-sm" />
                            <span className="text-sm font-medium text-gray-700">Check-out</span>
                          </div>
                          <p className="text-sm text-gray-600 ml-6">{FormatDate(stay.check_out_date)}</p>
                        </div>

                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <FaUserFriends className="text-gray-400 text-sm" />
                            <span className="text-sm font-medium text-gray-700">Cleaner</span>
                          </div>
                          <div className="ml-6">
                            {stay?.cleaner_name ? (
                              <p className="text-sm text-gray-600">{stay.cleaner_name}</p>
                            ) : (
                              <button
                                onClick={() => handleAddCleaner(stay)}
                                className="text-blue-600 hover:text-blue-800 text-sm underline"
                              >
                                Assign Cleaner
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                        <div className="text-sm text-gray-600">
                          {stay?.invoices[0]?.status && (
                            <span className={`px-2 py-1 rounded text-xs ${stay.invoices[0].status === 'approved' ? 'bg-green-100 text-green-800' :
                              stay.invoices[0].status === 'paid' ? 'bg-blue-100 text-blue-800' :
                                stay.invoices[0].status === 'rejected' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                              }`}>
                              Invoice: {stay.invoices[0].status.toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleAddCleaner(stay)}
                            className="text-gray-600 hover:text-gray-800 p-2 rounded-full hover:bg-gray-200 transition-colors"
                            title="View Details"
                          >
                            <MdOutlineRemoveRedEye className="text-lg" />
                          </button>
                          {stay.status === "ACCEPTED" && (
                            <>
                              <button
                                onClick={() => handleTaskSheetClick(stay.id)}
                                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                              >
                                Task Sheet
                              </button>
                              <button
                                onClick={() => handleInvoiceClick(stay.id)}
                                className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
                              >
                                Invoice
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    No upcoming stays found
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Setup Tasks & Quick Actions */}
        <div className="lg:w-80 w-full">
          {/* Setup Tasks */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-4">
            <div
              className={`p-4 border-b border-gray-200 rounded-t-lg ${completedTasks === 0 ? "bg-red-400" : "bg-[#FFF5CE]"
                }`}
            >
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <h3 className="font-bold text-[#586989] text-lg">Setup Tasks</h3>
                <div className="flex flex-col items-start gap-2 w-full sm:w-auto text-right bg-white rounded p-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Setup Progress</span>
                    <span className="text-sm font-bold text-[#586989]">{Math.round((completedTasks / 3) * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300  ${isSetupComplete ? "bg-green-500" : "bg-[#586989]"
                        }`}
                      style={{ width: `${completionPercentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm text-gray-600 whitespace-nowrap">
                    {completedTasks}/3 Completed
                  </span>
                </div>
              </div>
            </div>

            {/* Tasks List */}
            <div className="p-4 space-y-4">
              {/* Company Setup */}
              <div className={`p-3 rounded-lg border ${companyInfo ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                }`}>
                <div className="flex items-start gap-1">
                  {companyInfo ? (
                    <LiaCheckCircle className="text-green-500 text-xl mt-0.5 flex-shrink-0" />
                  ) : (
                    <PiNumberCircleOneLight className="text-red-500 text-xl mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-800">Complete your profile</h4>
                    <p className="text-sm text-gray-600 mt-1">Add your contact information and preferences</p>
                    {!companyInfo && (
                      <Link
                        to="/"
                        className="inline-block mt-2 text-sm bg-[#586989] text-white px-3 py-1 rounded"
                      >
                        Add Company
                      </Link>
                    )}
                  </div>
                </div>
              </div>

              {/* Property Setup */}
              <div
                className={`p-3 rounded-lg border ${PropertyInfo
                  ? "border-green-200 bg-green-50"
                  : "border-red-200 bg-red-50"
                  }`}
              >
                <div className="flex items-start gap-1">
                  {PropertyInfo ? (
                    <LiaCheckCircle className="text-green-500 text-xl mt-0.5 flex-shrink-0" />
                  ) : (
                    <PiNumberCircleTwoLight className="text-red-500 text-xl mt-0.5 flex-shrink-0" />
                  )}

                  <div className="flex-1">
                    <h4 className="font-medium text-gray-800">Add your first property</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Register property details and cleaning requirements
                    </p>

                    {!PropertyInfo && (
                      <div className="flex gap-1 mt-2">
                        <Link
                          to="/lodgify/1"
                          className="inline-block mt-2 text-sm bg-[#586989] text-white px-3 py-1 rounded"
                        >
                          Connect Channel Manager
                        </Link>

                        <Link
                          to="/property"
                          className="inline-block mt-2 text-sm bg-[#586989] text-white px-3 py-1 rounded"
                        >
                          Add Property
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>


              {/* Cleaner Setup */}
              <div className={`p-3 rounded-lg border ${CleanerInfo ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                }`}>
                <div className="flex items-start gap-1">
                  {CleanerInfo ? (
                    <LiaCheckCircle className="text-green-500 text-xl mt-0.5 flex-shrink-0" />
                  ) : (
                    <PiNumberCircleThreeLight className="text-red-500 text-xl mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-800">Invite cleaners</h4>
                    <p className="text-sm text-gray-600 mt-1">Build your team of trusted cleaners</p>
                    {!CleanerInfo && (
                      <Link
                        to="/invite-cleaner"
                        className="inline-block mt-2 text-sm bg-[#586989] text-white px-3 py-1 rounded"
                      >
                        Invite
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-bold text-[#586989]">Quick Actions</h3>
            </div>
            <div className="p-4 space-y-2">
              <Link
                to="/invite-cleaner"
                className="flex items-center gap-3 p-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <div className="">
                  <FiUser className="text-blue-500" />
                </div>
                <span className="font-medium text-gray-800">Invite Cleaner</span>
              </Link>
              <Link
                to="/other-tasks"
                className="flex items-center gap-3 p-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <div className="">
                  <FiEdit className="text-blue-500" />
                </div>
                <span className="font-medium text-gray-800">View Adhoc Tasks</span>
              </Link>
              <Link
                to="/settings/profile"
                className="flex items-center gap-3 p-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <div className="">
                  <FiSettings className="text-blue-500" />
                </div>
                <span className="font-medium text-gray-800">Settings</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Invoices for Approval Section */}
      <div className="w-full">
        {invoicesForApproval.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-[#586989]">Invoices for Approval</h2>
              {/* <button
                onClick={handleViewAllInvoices}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                View All <FaArrowRight className="text-xs" />
              </button> */}
            </div>
            <div className="p-4">
              <div className="space-y-4">
                {invoicesForApproval.map((invoice, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <div className="bg-gray-100 px-3 py-1 rounded border">
                          <span className="text-sm font-medium text-gray-700">{invoice.invoiceNumber}</span>
                        </div>
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                          {invoice.status}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <FaUser className="text-gray-400 text-sm" />
                          <span className="text-sm font-medium text-gray-700">{invoice.cleanerName}</span>
                        </div>
                        <p className="text-sm text-gray-600 ml-6">{invoice.serviceType}</p>
                        <p className="text-sm text-gray-500 ml-6">{invoice.cleanerEmail}</p>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <FaHome className="text-gray-400 text-sm" />
                          <span className="text-sm font-medium text-gray-700">{invoice.propertyName}</span>
                        </div>
                        <p className="text-sm text-gray-600 ml-6">Submitted: {invoice.completionDate}</p>
                      </div>
                    </div>

                    {invoice.comments && (
                      <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Comments:</p>
                        <div
                          className="text-sm text-gray-600 prose prose-sm max-w-none"
                          style={{
                            lineHeight: '1.4',
                            wordWrap: 'break-word'
                          }}
                          dangerouslySetInnerHTML={{
                            __html: invoice.comments
                              .replace(/(<br\s*\/?>\s*){3,}/gi, '<br /><br />') // Limit consecutive line breaks
                              .replace(/(<p><br><\/p>)+/gi, '') // Remove empty paragraphs with breaks
                              .replace(/>\s+</g, '><') // Remove whitespace between tags
                              .replace(/\s+/g, ' ') // Normalize internal whitespace
                              .trim()
                          }}
                        />
                      </div>
                    )}

                    <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                      <div className="text-center  items-center justify-center">
                        <p className="text-2xl font-bold  text-gray-800">£{invoice.amount}</p>
                        <p className="text-sm text-gray-600">Subtotal: £{invoice.subtotal}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleRejectInvoice(invoice)}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
                        >
                          Reject
                        </button>
                        <button
                          onClick={() => handleApproveInvoice(invoice.id)}
                          className="px-4 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                        >
                          Approve
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Comment Modal for Reject */}
      <CommentModal
        isOpenModal={isOpenModal}
        setIsOpenModal={setIsOpenModal}
        invoiceId={selectedInvoice?.id}
        onRejectConfirm={handleRejectConfirm}
      />
    </div>
  );
}